import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { getHotelList, HotelListItem } from "@/api/hotel";
import { useLocationSelect } from "@/hooks/useLocationSelect";
import Header from "./components/Header";
import HotelCard from "./components/HotelCard";
import ReplaceModeTip from "./components/ReplaceModeTip";
import ConfirmModal from "./components/ConfirmModal";

const PAGE_SIZE = 10;

/** 排序值映射 */
const SORT_MAP: Record<
  number,
  "distance" | "price" | "price_desc" | "recommend"
> = {
  1: "recommend",
  2: "price",
  3: "distance",
};

/** 价格区间映射 [min, max] 或 null(不限) */
const PRICE_RANGE_MAP: Record<number, [number | null, number | null]> = {
  0: [null, null],
  1: [0, 500],
  2: [500, 1000],
  3: [1000, 2000],
  4: [2000, null],
};

export default function HotelListPage() {
  const navigate = useNavigate();
  const routerLocation = useLocation();
  const isReplaceMode =
    (routerLocation.state as { replace?: boolean })?.replace === true;

  // 使用 hook 获取位置信息
  const { currentCity, coords, locationDisplay, loading: locationLoading } = useLocationSelect();

  // 状态
  const [selected, setSelected] = useState<number | null>(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [activeSort, setActiveSort] = useState(0);
  const [priceRange, setPriceRange] = useState(0);
  const [selectedStars, setSelectedStars] = useState<number[]>([]);
  const [showFilter, setShowFilter] = useState(false);

  // 列表状态
  const [hotelList, setHotelList] = useState<HotelListItem[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const initializedRef = useRef(false);

  /** 构建请求参数并拉取列表数据 */
  const fetchHotelList = useCallback(
    async (pageNum: number, isLoadMore = false) => {
      try {
        if (!isLoadMore && pageNum === 1) setLoading(true);
        else if (isLoadMore) setLoadingMore(true);

        const starStr =
          selectedStars.length > 0 ? selectedStars.join(",") : undefined;
        const [minP, maxP] = PRICE_RANGE_MAP[priceRange];

        const { data } = await getHotelList({
          page: pageNum,
          limit: PAGE_SIZE,
          city:
            currentCity !== "定位中..." && currentCity !== "未知位置"
              ? currentCity
              : undefined,
          star_rating: starStr,
          min_price: minP ?? undefined,
          max_price: maxP ?? undefined,
          longitude: coords.longitude || undefined,
          latitude: coords.latitude || undefined,
          sort: activeSort === 0 ? undefined : SORT_MAP[activeSort],
        });

        const list = data.data.list;
        const total = data.data.total;

        if (isLoadMore) {
          setHotelList((prev) => [...prev, ...list]);
        } else {
          setHotelList(list);
        }
        setTotalCount(total);
        setHasMore(list.length >= PAGE_SIZE);
        setPage(pageNum);
      } catch (e) {
        console.error("[酒店列表] 获取失败:", e);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [
      currentCity,
      activeSort,
      priceRange,
      selectedStars,
      coords.longitude,
      coords.latitude,
    ],
  );

  // 初始加载 + 筛选变化重新请求
  useEffect(() => {
    // 首次渲染时 currentCity 还是 "定位中..."，等位置确定后再请求
    if (!initializedRef.current) {
      if (currentCity === "定位中..." || currentCity === "未知位置") return;
      initializedRef.current = true;
    }
    fetchHotelList(1);
  }, [fetchHotelList, currentCity]);

  // 上拉加载更多
  const handleLoadMore = useCallback(() => {
    if (loadingMore || !hasMore) return;
    fetchHotelList(page + 1, true);
  }, [loadingMore, hasMore, page, fetchHotelList]);

  useEffect(() => {
    const el = loadMoreRef.current;
    if (!el || !hasMore || loadingMore) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) handleLoadMore();
      },
      { rootMargin: "100px" },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasMore, loadingMore, handleLoadMore]);

  // 星级多选
  const toggleStar = (v: number) =>
    setSelectedStars((prev) =>
      prev.includes(v) ? prev.filter((s) => s !== v) : [...prev, v],
    );

  const handleLocationClick = () =>
    navigate("/location-select", { state: { from: "/hotels" } });

  const handleSelect = (id: number) => {
    if (isReplaceMode) {
      setSelected(id);
      setShowConfirm(true);
    } else {
      navigate("/hotel-detail-product", { state: { hotelId: id } });
    }
  };

  const handleConfirm = () => {
    setShowConfirm(false);
    navigate("/ai-plan");
  };

  return (
    <div
      className="min-h-screen pb-10"
      style={{
        background: "#F8F9FC",
        fontFamily: "'Inter','SF Pro Text',sans-serif",
      }}
    >
      <Header
        city={locationDisplay}
        showFilter={showFilter}
        activeSort={activeSort}
        priceRange={priceRange}
        selectedStars={selectedStars}
        onToggleFilter={() => setShowFilter(!showFilter)}
        onSortChange={setActiveSort}
        onPriceChange={setPriceRange}
        onStarToggle={toggleStar}
        onLocationClick={handleLocationClick}
      />

      {isReplaceMode && <ReplaceModeTip />}

      <div className="px-4 pt-3 pb-1 flex items-center justify-between">
        <span className="text-xs text-gray-400">
          共找到{" "}
          <span className="font-semibold text-gray-700">{totalCount}</span>{" "}
          家酒店
        </span>
        <span className="text-xs text-gray-400">{locationDisplay}</span>
      </div>

      <div className="px-4 py-2 space-y-3">
        {/* 加载骨架屏 */}
        {(loading || locationLoading) &&
          Array.from({ length: 3 }).map((_, i) => (
            <div
              key={`skeleton-${i}`}
              className="bg-white rounded-2xl overflow-hidden animate-pulse"
              style={{ height: 126 }}
            >
              <div className="flex h-full">
                <div style={{ width: 100 }} className="bg-gray-200" />
                <div className="flex-1 p-3 space-y-2">
                  <div className="h-4 bg-gray-200 rounded w-3/4" />
                  <div className="h-3 bg-gray-100 rounded w-1/2" />
                  <div className="h-3 bg-gray-100 rounded w-full" />
                  <div className="h-6 bg-gray-100 rounded w-20 ml-auto mt-auto" />
                </div>
              </div>
            </div>
          ))}

        {/* 酒店卡片 */}
        {!(loading || locationLoading) &&
          hotelList.map((hotel) => (
            <HotelCard key={hotel.id} hotel={hotel} onClick={handleSelect} />
          ))}

        {/* 空状态 */}
        {!(loading || locationLoading) && hotelList.length === 0 && (
          <div className="py-16 flex flex-col items-center gap-3">
            <i className="ri-hotel-line text-4xl text-gray-300" />
            <span className="text-sm text-gray-400">暂无符合条件的酒店</span>
          </div>
        )}

        {/* 加载更多 */}
        {!(loading || locationLoading) && (
          <div
            ref={loadMoreRef}
            className="py-6 flex flex-col items-center justify-center gap-2"
          >
            {loadingMore && (
              <>
                <div className="w-5 h-5 border-2 border-gray-300 border-t-blue-600 rounded-full animate-spin" />
                <span className="text-xs text-gray-400">加载中...</span>
              </>
            )}
            {!hasMore && hotelList.length > 0 && (
              <span className="text-xs text-gray-300">— 已经到底了 —</span>
            )}
          </div>
        )}
      </div>

      <ConfirmModal
        visible={showConfirm}
        hotelName={hotelList.find((h) => h.id === selected)?.hotel_name}
        onCancel={() => setShowConfirm(false)}
        onConfirm={handleConfirm}
      />
    </div>
  );
}
